import type { Organization, OrganizationBranding, OrganizationType } from '../../../types/access'

export const formatOrgType = (orgType?: string) => (
  (orgType || 'organization')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
)

export const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString()
}

export const brandingOrgId = (branding: OrganizationBranding) => branding.orgId || branding.org_id || ''

export const brandingName = (branding?: OrganizationBranding, fallback?: string) => (
  branding?.displayName || branding?.display_name || fallback || 'Unnamed organization'
)

export const brandingLogo = (branding?: OrganizationBranding) => (
  branding?.logoUrl
  || branding?.logo_url
  || branding?.logoAssetUrl
  || branding?.logo_asset_url
  || ((branding?.logoFileId || branding?.logo_file_id) && brandingOrgId(branding) ? `/api/orgs/${brandingOrgId(branding)}/branding/logo/file` : '')
  || ''
)

export const brandingBanner = (branding?: OrganizationBranding) => (
  branding?.bannerUrl
  || branding?.banner_url
  || branding?.bannerAssetUrl
  || branding?.banner_asset_url
  || ((branding?.bannerFileId || branding?.banner_file_id) && brandingOrgId(branding) ? `/api/orgs/${brandingOrgId(branding)}/branding/banner/file` : '')
  || ''
)

export const brandingShortName = (branding?: OrganizationBranding, fallback?: string) => (
  branding?.shortName || branding?.short_name || brandingName(branding, fallback)
)

export const brandingField = (
  branding: OrganizationBranding | undefined,
  camelKey: keyof OrganizationBranding,
  snakeKey: keyof OrganizationBranding,
) => (branding?.[camelKey] || branding?.[snakeKey] || '') as string

export const brandingColor = (branding: OrganizationBranding | undefined, key: 'primary' | 'secondary' | 'accent') => {
  if (!branding) return ''
  if (key === 'primary') return branding.primaryColor || branding.primary_color || ''
  if (key === 'secondary') return branding.secondaryColor || branding.secondary_color || ''
  return branding.accentColor || branding.accent_color || ''
}

export const brandingThemeColor = (
  branding: OrganizationBranding | undefined,
  key: 'background' | 'surface' | 'text' | 'button' | 'buttonText',
) => {
  if (!branding) return ''
  if (key === 'background') return branding.backgroundColor || branding.background_color || branding.secondaryColor || branding.secondary_color || ''
  if (key === 'surface') return branding.surfaceColor || branding.surface_color || ''
  if (key === 'text') return branding.textColor || branding.text_color || ''
  if (key === 'button') return branding.buttonColor || branding.button_color || branding.primaryColor || branding.primary_color || ''
  return branding.buttonTextColor || branding.button_text_color || ''
}

export const brandColors = (branding?: OrganizationBranding) => (
  [brandingColor(branding, 'primary'), brandingColor(branding, 'secondary'), brandingColor(branding, 'accent')]
    .filter((color): color is string => Boolean(color))
)

export const countByOrgType = (organizations: Organization[]) => organizations.reduce<Record<string, number>>((counts, org) => {
  counts[org.org_type] = (counts[org.org_type] || 0) + 1
  return counts
}, {})

export const organizationLabel = (organizations: Organization[], orgId?: string | null) => {
  if (!orgId) return 'Tenant'
  const org = organizations.find((item) => item.id === orgId)
  return org ? `${org.name} (${formatOrgType(org.org_type)})` : orgId.slice(0, 8)
}

export const orgTypeOptions: Array<{ value: OrganizationType; label: string }> = [
  { value: 'agency', label: 'Agency' },
  { value: 'company', label: 'Company' },
  { value: 'platform_owner', label: 'Platform owner' },
]
